/**
 * AssessmentPdfDrawer
 *
 * Slide-in PDF drawer for QuickAssess references.
 *
 * Highlight strategy (priority order):
 *   1. verbatimExcerpt  — exact text returned by backend AI matching (new sessions).
 *      Split on ? to separate Q from A, then use 5-word sliding windows on long
 *      phrases so every keyword fits within a single rendered PDF line.
 *      (The viewer search plugin CANNOT match text across line breaks.)
 *   2. n-gram fallback  — for old sessions without verbatim_excerpt.
 */

import React, { useEffect, useState } from "react";
import crossIcon from "../../assets/img/Icon.svg";
import Test from "./Test";

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Strategy 1 — verbatim excerpt (new sessions).
 *
 * PDF table cells wrap at ~7 words per line. The search plugin matches within
 * a single line only, so:
 *   - Split on `?` → separates question text from answer text
 *   - Keep each part whole if ≤7 words (fits on one line)
 *   - Slide a 5-word window across longer parts so every chunk fits on one line
 */
function keywordsFromVerbatim(text) {
  if (!text || typeof text !== "string") return [];

  const keywords = [];

  // Split on ? — left side = question, right side = answer
  const parts = text.split("?").map((p) => p.trim()).filter(Boolean);

  for (const part of parts) {
    // Also split on embedded newlines
    const lines = part.split(/\n/).map((l) => l.trim()).filter(Boolean);

    for (const line of lines) {
      const words = line.split(/\s+/).filter(Boolean);
      if (words.length <= 1) continue;

      if (words.length <= 7) {
        // Short enough to live on one PDF line — use as-is
        keywords.push(line);
      } else {
        // Long phrase — slide a 5-word window so each chunk fits one line
        for (let i = 0; i <= words.length - 5; i++) {
          keywords.push(words.slice(i, i + 5).join(" "));
        }
      }
    }
  }

  // Deduplicate, preserving order; cap to avoid flooding the viewer
  const seen = new Set();
  return keywords.filter((k) => {
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  }).slice(0, 12);
}

/**
 * Strategy 2 — n-gram fallback (old sessions without verbatim_excerpt).
 * Generates sliding 3-6 word windows from the raw excerpt text.
 */
function keywordsFromNgram(excerptText) {
  if (!excerptText || typeof excerptText !== "string") return [];

  const candidates = new Set();

  excerptText.split("?").forEach((part) => {
    const clean = part.replace(/\s+/g, " ").trim();
    if (clean.length > 3 && !/^\d+$/.test(clean)) candidates.add(clean);
  });

  const words = excerptText.replace(/[?\n\r]/g, " ").split(/\s+/).filter(Boolean);
  for (let size = 6; size >= 3; size--) {
    for (let i = 0; i <= words.length - size; i++) {
      const phrase = words.slice(i, i + size).join(" ");
      if (!/^\d+$/.test(phrase)) candidates.add(phrase);
    }
  }

  let filtered = Array.from(candidates).filter((c) => c.split(" ").length > 2);
  filtered = filtered.filter(
    (c) => !filtered.some((other) => other.length > c.length && other.includes(c))
  );
  return filtered.sort((a, b) => b.length - a.length).slice(0, 8);
}

// ── Styles ────────────────────────────────────────────────────────────────────

const drawerStyles = (isOpen) => ({
  position: "fixed",
  right: isOpen ? "0" : "-100%",
  top: "0",
  width: "50vw",
  height: "100vh",
  background: "white",
  boxShadow: "2px 0 5px rgba(0,0,0,0.3)",
  transition: "right 0.4s ease-in-out",
  zIndex: 999,
  overflowY: "auto",
});

const closeButtonStyles = {
  position: "absolute",
  top: "5px",
  right: "5px",
  color: "white",
  border: "none",
  cursor: "pointer",
  padding: "6px 12px",
  fontSize: "15px",
  zIndex: 1009,
};

// ── Component ─────────────────────────────────────────────────────────────────

const AssessmentPdfDrawer = ({ isOpen, onClose, pdfUrl, excerpt, verbatimExcerpt }) => {
  const [keywords, setKeywords] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setKeywords([]);
      return;
    }

    if (verbatimExcerpt && verbatimExcerpt.trim().length > 3) {
      // Strategy 1: use verbatim text from backend
      const kws = keywordsFromVerbatim(verbatimExcerpt);
      setKeywords(kws.length > 0 ? kws : keywordsFromNgram(excerpt));
    } else if (excerpt) {
      // Strategy 2: n-gram fallback for old sessions
      setKeywords(keywordsFromNgram(excerpt));
    } else {
      setKeywords([]);
    }
  }, [isOpen, excerpt, verbatimExcerpt]);

  useEffect(() => {
    if (!isOpen) setKeywords([]);
  }, [isOpen]);

  if (!pdfUrl) return null;

  return (
    <div style={drawerStyles(isOpen)}>
      <button style={closeButtonStyles} onClick={onClose} aria-label="Close PDF drawer">
        <img src={crossIcon} alt="Close" />
      </button>
      <Test keywords={keywords} pdfUrl={pdfUrl} />
    </div>
  );
};

export default AssessmentPdfDrawer;